import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./motion";
import { StoreBadge } from "./ui";

const ROUTE_MAP = {
  Contact: "/contact",
  "Trust & Safety": "/#safety",
};

const COLS = [
  { title: "Product", links: ["How it works", "Safety", "The App", "Pricing"] },
  { title: "Company", links: ["About", "Careers", "Press", "Blog"] },
  { title: "Support", links: ["Help center", "Contact", "Community", "Trust & Safety"] },
];

export const Footer = () => {
  return (
    <footer id="download" className="relative overflow-hidden border-t border-[#12142b]/10 bg-[#f4f6fb] pt-24">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        {/* CTA */}
        <Reveal className="mb-16 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-6 font-display text-xs font-semibold uppercase tracking-[0.22em] text-[#2340b5]">
              Ready when you are
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-[#12142b] sm:text-4xl">
              Download & book your first ride.
            </h2>
          </div>
          <div className="flex flex-wrap gap-4">
            <StoreBadge store="apple" testId="footer-appstore-btn" />
            <StoreBadge store="google" testId="footer-googleplay-btn" />
          </div>
        </Reveal>

        {/* Giant wordmark */}
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="select-none font-display text-[22vw] font-black leading-[0.8] tracking-tighter text-[#12142b] lg:text-[16vw]"
        >
          LET'S <span className="text-[#2340b5]">RIDE</span>
        </motion.h2>

        {/* Link columns */}
        <div className="mt-16 grid grid-cols-2 gap-10 border-t border-[#12142b]/10 py-14 md:grid-cols-4">
          <div>
            <img src="/saferide-logo.png" alt="SafeRide — Community carpooling" className="h-9 w-auto" />
            <p className="mt-5 max-w-[220px] text-sm leading-relaxed text-[#565b73]">
              Inter-city carpooling that connects verified drivers and riders across Canada.
            </p>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <h4 className="mb-5 font-display text-xs font-semibold uppercase tracking-[0.2em] text-[#12142b]/50">
                {col.title}
              </h4>
              <ul className="space-y-3">
                {col.links.map((l) => {
                  const to = ROUTE_MAP[l];
                  const cls =
                    "group inline-flex items-center gap-1 text-sm text-[#565b73] transition-colors hover:text-[#2340b5]";
                  const arrow = (
                    <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                  );
                  return (
                    <li key={l}>
                      {to ? (
                        <Link to={to} className={cls}>
                          {l}
                          {arrow}
                        </Link>
                      ) : (
                        <a href="#top" className={cls}>
                          {l}
                          {arrow}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-[#12142b]/10 py-8 text-xs text-[#565b73] sm:flex-row">
          <span>
            <button
              type="button"
              data-testid="management-login-trigger"
              onClick={() => window.open("https://www.mysaferide.ca", "_blank", "noopener,noreferrer")}
              aria-label="Management login"
              title="Management login"
              className="cursor-default bg-transparent p-0 text-inherit focus:outline-none"
            >
              ©
            </button>{" "}
            {new Date().getFullYear()} SafeRide. All rights reserved.
          </span>
          <div className="flex gap-6">
            <Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors hover:text-[#12142b]">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="transition-colors hover:text-[#12142b]">Terms</Link>
            <a href="#top" className="transition-colors hover:text-[#12142b]">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
